import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Popover, Typography } from "@material-ui/core";
import { CheckIcon } from "../ChatiaIcons";

const ChevronDownIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const radius = ds.radius || { sm: 10, md: 12 };
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    trigger: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      width: "100%",
      borderRadius: radius.md,
      border: "1px solid transparent",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: isDark
        ? "0 2px 8px rgba(0,0,0,0.35)"
        : "0 2px 8px rgba(0,0,0,0.08)",
      transition: "filter 0.15s, box-shadow 0.15s",
      "&:hover": {
        filter: "brightness(1.05)",
      },
      "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
    },
    triggerLabel: {
      flex: 1,
      textAlign: "left",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    triggerPlaceholder: {
      opacity: 0.85,
      fontWeight: 500,
    },
    triggerLeading: {
      display: "inline-flex",
      alignItems: "center",
    },
    paper: {
      borderRadius: 14,
      minWidth: 230,
      overflow: "hidden",
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.06)",
      background: isDark ? "#121212" : "#ffffff",
    },
    list: {
      padding: 6,
      maxHeight: 360,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    optionItem: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "9px 12px",
      marginBottom: 4,
      borderRadius: 8,
      cursor: "pointer",
      transition: "background 0.15s ease",
      color: isDark ? "#ffffff" : "#1a1a1a",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      "&:last-child": { marginBottom: 0 },
    },
    optionItemSelected: {
      background: "rgba(6, 207, 156, 0.10)",
      "&:hover": {
        background: "rgba(6, 207, 156, 0.15)",
      },
    },
    optionItemDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      "&:hover": { background: "transparent" },
    },
    optionLeading: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
    },
    optionLabel: {
      flex: 1,
      fontSize: 14,
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    optionTrailing: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
    },
    optionCheck: {
      width: 18,
      height: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#06cf9c",
      flexShrink: 0,
    },
    placeholderItem: {
      fontStyle: "italic",
      opacity: 0.7,
    },
    fieldLabel: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: isDark ? "#8696a0" : "#667781",
      marginBottom: 4,
      display: "block",
    },
    wrapper: {
      width: "100%",
    },
  };
});

/**
 * Select padronizado — trigger na cor de destaque (whitelabel) com bordas 12px,
 * dropdown estilo do seletor de idioma (sem header, só lista de opções).
 *
 * Props:
 *   value, onChange, label, placeholder, disabled, fullWidth (default true)
 *   options: [{ value, label, icon?, trailing? }]
 */
const ChatiaSelect = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  fullWidth = true,
  options = [],
  className,
  style,
}) => {
  const classes = useStyles();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOpt = options.find(
    (o) => String(o.value) === String(value ?? "")
  );

  const handleSelect = (val) => {
    if (typeof onChange === "function") {
      onChange({ target: { value: val } });
    }
    setOpen(false);
  };

  const triggerStyle = {
    ...(fullWidth ? { width: "100%" } : { width: "auto" }),
    ...(style || {}),
  };

  return (
    <div className={classes.wrapper} style={fullWidth ? undefined : { width: "auto", display: "inline-block" }}>
      {label && <span className={classes.fieldLabel}>{label}</span>}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={`${classes.trigger} ${className || ""}`}
        style={triggerStyle}
        onClick={() => !disabled && setOpen((p) => !p)}
      >
        {selectedOpt?.icon && (
          <span className={classes.triggerLeading}>{selectedOpt.icon}</span>
        )}
        <span className={`${classes.triggerLabel} ${!selectedOpt ? classes.triggerPlaceholder : ""}`}>
          {selectedOpt ? selectedOpt.label : (placeholder || "")}
        </span>
        <ChevronDownIcon style={{ flexShrink: 0, opacity: 0.85 }} />
      </button>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: classes.paper,
          style: { marginTop: 4, minWidth: triggerRef.current?.offsetWidth || 230 },
        }}
        disableScrollLock
      >
        <div className={classes.list}>
          {placeholder && (
            <div
              className={`${classes.optionItem} ${classes.placeholderItem} ${!selectedOpt ? classes.optionItemSelected : ""}`}
              onClick={() => handleSelect("")}
            >
              <span className={classes.optionLabel}>{placeholder}</span>
              <div className={classes.optionCheck}>
                {!selectedOpt && <CheckIcon />}
              </div>
            </div>
          )}
          {options.map((opt) => {
            const isSelected = selectedOpt && String(opt.value) === String(selectedOpt.value);
            return (
              <div
                key={String(opt.value)}
                className={`${classes.optionItem} ${isSelected ? classes.optionItemSelected : ""}`}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.icon && (
                  <span className={classes.optionLeading}>
                    {typeof opt.icon === "string" ? (
                      <img
                        src={opt.icon}
                        alt=""
                        style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 3 }}
                      />
                    ) : (
                      opt.icon
                    )}
                  </span>
                )}
                <Typography className={classes.optionLabel}>{opt.label}</Typography>
                {opt.trailing && (
                  <span className={classes.optionTrailing}>{opt.trailing}</span>
                )}
                <div className={classes.optionCheck}>
                  {isSelected && <CheckIcon />}
                </div>
              </div>
            );
          })}
        </div>
      </Popover>
    </div>
  );
};

export default ChatiaSelect;
